package game

import com.soywiz.kds.FastArrayList
import com.soywiz.kds.iterators.fastForEachWithIndex
import com.soywiz.korau.sound.Sound
import com.soywiz.korau.sound.SoundChannel
import com.soywiz.korge.input.Input
import com.soywiz.korge.view.Views
import com.soywiz.korio.async.launchImmediately
import com.soywiz.korio.lang.format
import game.controller.*
import game.entity.Direction
import kotlinx.coroutines.CoroutineScope
import view.CharMouseEvent
import view.Terminal
import kotlin.math.sqrt

const val ROOM_TOP_ROW = 1
const val ROOM_LEFT_COL = 28

class Game(
    val settings: GameSettings,
    val strings: Array<Map<String, String>>,
    val font: CgaFont,
    val rooms: List<Room>,
    val sprites: List<CharSprite>,
    val sounds: Map<String, Sound>,
    val views: Views,
) : CoroutineScope by views {
    var shards = 0
    val player = Player()
    private val permanentlyRemoved = mutableSetOf<String>()
    private val lore = mutableSetOf<String>()
    var message = ""
        set(value) {
            field = value
            messageCoolDown = 0
        }
    var messageCoolDown = 0
    var saveId = SaveId.Save1

    var transitionController = ScrollTransitionController()
    var controller: GameController = PlayController()
        set(value) {
            val oldController = field
            oldController.deactivate(this)
            value.nextController = oldController
            field = value
            field.activate(this)
        }

    var currentRoomIndex: Int = 0
    private var soundChannel: SoundChannel? = null

    val currentRoom: Room
        get() = rooms[currentRoomIndex]

    val shardCount: Int
        get() = rooms.flatMap {
            it.entityDef.map {
                when (it.type) {
                    "small-shard" -> 1
                    "medium-shard" -> 5
                    "large-shard" -> 15
                    else -> 0
                }
            }
        }.sum()

    val loreCount: Int
        get() = rooms.sumOf { it.loreCount }

    val collectedLoreCount: Int
        get() = lore.size

    val collectedAllLore: Boolean
        get() = collectedLoreCount >= loreCount

    private val roomCache = FastArrayList<Room>()

    private var running = true

    init {
        controller = TitleController()
        player.init(this)
    }

    fun text(id: String, vararg params: Any): String {
        val languageOrdinal = settings.language.ordinal
        val text = strings[languageOrdinal][id] ?: id

        return text.format(*params)
    }

    fun textOrNull(id: String, vararg params: Any): String? = strings[settings.language.ordinal][id]?.format(*params)

    fun renderTileType(terminal: Terminal, colOffset: Int, rowOffset: Int) {
        currentRoom.render(terminal, colOffset, rowOffset)

        for (row in 0 until currentRoom.height) {
            for (col in 0 until currentRoom.width) {
                val tile = currentRoom[col, row]
                with(terminal.getChar(col + colOffset, row + rowOffset)) {
                    fgColor = tile.type.fgColor
                    bgColor = tile.type.bgColor
                }
            }
        }
    }

    fun tick(terminal: Terminal, input: Input) {
        if (running) {
            controller.tick(this, input)
            controller.render(this, terminal)
        } else {
            terminal.stage?.views?.gameWindow?.close()
        }
    }

    fun frame(terminal: Terminal, input: Input) {
        controller.frame(this, input)
    }

    fun renderAll(terminal: Terminal) {
        renderUi(terminal)
        renderStage(terminal)
    }

    fun renderCircle(terminal: Terminal, radius: Int, pointCol: Int, pointRow: Int) {
        for (row in 0 until ROOM_HEIGHT) {
            for (col in 0 until ROOM_WIDTH) {
                if (col != pointCol || row != pointRow) {
                    val deltaCol = col - pointCol
                    val deltaRow = row - pointRow
                    val squareCol = deltaCol * deltaCol
                    val squareRow = deltaRow * deltaRow
                    val distance = sqrt(squareCol.toFloat() + squareRow.toFloat()).toInt()
                    if (distance >= radius)
                        with(terminal.getChar(col + 28, row + 1)) {
                            fgColor = CgaColor.BLACK.rgba
                            bgColor = CgaColor.BLACK.rgba
                        }
                }
            }
        }
    }

    fun renderStage(terminal: Terminal, renderPlayer: Boolean = true) {
        currentRoom.render(
            terminal,
            ROOM_LEFT_COL,
            ROOM_TOP_ROW,
            0,
            0,
            ROOM_WIDTH,
            ROOM_HEIGHT,
            currentRoom.dark,
            player.lightSource.radius,
            player.col,
            player.row,
            player.life == 0
        )
        if (renderPlayer) {
            renderPlayer(terminal)
        }
    }

    fun renderPlayer(terminal: Terminal) {
        player.render(terminal, 28, 1)
    }

    fun renderUi(terminal: Terminal) {
        terminal.fillRect(0, 0, 79, 24, 32, CgaColor.WHITE, CgaColor.BLACK)

        terminal.drawDoubleBorder(0, 0, 79, 24, CgaColor.LIGHT_GRAY, CgaColor.BLACK)
        terminal.drawSingleVLine(27, 1, 23, CgaColor.LIGHT_GRAY, CgaColor.BLACK)
        terminal.drawSingleHLine(22, 28, 78, CgaColor.LIGHT_GRAY, CgaColor.BLACK)

        terminal.setChar(27, 0, char = 0xD1, CgaColor.LIGHT_GRAY, CgaColor.BLACK)
        terminal.setChar(27, 24, char = 0xCF, CgaColor.LIGHT_GRAY, CgaColor.BLACK)
        terminal.setChar(27, 22, char = 0xC3, CgaColor.LIGHT_GRAY, CgaColor.BLACK)
        terminal.setChar(79, 22, char = 0xB6, CgaColor.LIGHT_GRAY, CgaColor.BLACK)
        terminal.setChar(74, 22, 0xC2, CgaColor.LIGHT_GRAY, CgaColor.BLACK)
        terminal.setChar(74, 23, 0xB3, CgaColor.LIGHT_GRAY, CgaColor.BLACK)
        terminal.setChar(74, 24, 0xCF, CgaColor.LIGHT_GRAY, CgaColor.BLACK)

        renderSideBar(terminal)

        renderMessage(terminal)
    }

    val currentRoomName: String
        get() = roomName(currentRoom)

    private fun roomName(room: Room): String = roomName(room.id)
    private fun roomName(roomId: String): String = text("${roomId}_Name")

    private fun renderSideBar(terminal: Terminal) {
        terminal.drawString(2, 2, text("SideBar_Location"), CgaColor.LIGHT_GRAY, CgaColor.BLACK)
        terminal.drawString(2, 3, currentRoomName, CgaColor.WHITE, CgaColor.BLACK)

        terminal.drawString(2, 5, text("SideBar_Life"), CgaColor.LIGHT_GRAY, CgaColor.BLACK)
        terminal.setChar(2, 6, 0xB5, CgaColor.LIGHT_BLUE, CgaColor.BLACK)
        terminal.setChar(3 + player.maxLife, 6, 0xC6, CgaColor.LIGHT_BLUE, CgaColor.BLACK)
        for (i in 1..player.maxLife) {
            val char = if (i <= player.life) {
                0x03
            } else {
                0x09
            }

            terminal.setChar(2 + i, 6, char, CgaColor.LIGHT_RED, CgaColor.BLACK)
        }

        if (player.lens) {
            terminal.drawString(2, 8, text("SideBar_Shards"), CgaColor.LIGHT_GRAY, CgaColor.BLACK)
            terminal.drawString(2, 9, "$shards/1000", CgaColor.WHITE, CgaColor.BLACK)
        }
    }

    fun getRoomById(id: String): Room = rooms.filter { it.id == id }[0]
    fun getSpriteById(id: String): CharSprite = sprites.filter { it.id == id }[0]


    fun loadRoom(id: String) {
        currentRoomIndex = -1

        rooms.fastForEachWithIndex { i, it ->
            if (it.id == id) {
                currentRoomIndex = i
            }
        }

        if (currentRoomIndex == -1) {
            throw Exception("Invalid room id: $id")
        }

        val roomCacheSize = 2
        roomCache.remove(currentRoom)
        roomCache.add(0, currentRoom) // Move current room back to top...
        if (roomCache.size > roomCacheSize) {
            roomCache[roomCacheSize].exit(this)
            roomCache.removeAt(roomCacheSize)
        }
        currentRoom.enter(this)
    }

    fun getSound(id: String): Sound = sounds.getValue(id)

    fun playSound(sound: Sound) {
        if (!settings.mute) {
            soundChannel = sound.play(views.coroutineContext)
        }
    }

    private fun renderMessage(terminal: Terminal) {
        if (messageCoolDown < 2000) {
            messageCoolDown++
        }

        val fgColor = when {
            messageCoolDown < 15 -> CgaColor.YELLOW
            messageCoolDown < 500 -> CgaColor.WHITE
            messageCoolDown < 700 -> CgaColor.LIGHT_GRAY
            messageCoolDown < 1500 -> CgaColor.DARK_GRAY
            else -> CgaColor.BLACK
        }

        for (i in 0..45) {
            var char = ' '.code

            if (i < message.length) {
                char = message[i].code
            }

            terminal.setChar(i + 28, 23, char, fgColor, CgaColor.BLACK)
        }
    }

    fun renderCurrentRoom(terminal: Terminal, col: Int, row: Int) {
        currentRoom.renderTiles(terminal, col, row)
    }

    fun startScrollTransition(toRoomId: String, type: Direction) {
        if (currentRoom[player.col, player.row].type != TileType.Lava) {
            saveGame(saveId)
        }

        val sourceRoom = currentRoom
        loadRoom(toRoomId)
        val destRoom = currentRoom
        transitionController.startScrollTransition(sourceRoom, destRoom, type)
        controller = transitionController
    }

    fun playSound(soundId: String) {
        playSound(getSound(soundId))
    }

    fun showDialog(dialogMessageId: String): DialogController {
        val dialogController = DialogController()

        dialogController.message = text(dialogMessageId)

        controller = dialogController

        return dialogController
    }

    fun startTeleportTransition(
        startCol: Int,
        startRow: Int,
        endCol: Int,
        endRow: Int,
        roomId: String,
        renderPlayer: Boolean
    ): TeleportTransitionController {
        saveGame(saveId)

        val teleportController = TeleportTransitionController()
        val room = getRoomById(roomId)

        teleportController.startTeleport(startCol, startRow, endCol, endRow, room, renderPlayer)

        controller = teleportController

        return teleportController
    }

    fun renderRoom(terminal: Terminal, redden: Boolean = false) =
        currentRoom.render(terminal, ROOM_LEFT_COL, ROOM_TOP_ROW, redden = redden)

    private fun getGameState(): GameState {
        val gameState = GameState().also {
            it.shards = shards
            it.playerLife = player.life
            it.playerMaxLife = player.maxLife
            it.playerCol = player.col
            it.playerRow = player.row
            it.roomId = currentRoom.id

            it.lens = player.lens
            it.map = player.map
            it.shardScanner = player.shardScanner
            it.loreScanner = player.loreScanner
            it.itemScanner = player.itemScanner
            it.flippers = player.flippers
            it.lightSource = player.lightSource
            it.weapon = player.weapon
            it.armor = player.armor
            it.bracer = player.bracer
            it.jump = player.jump
            it.speed = player.speed
            it.lavaBoots = player.lavaBoots
            it.divineBoots = player.divineBoots

            it.permanentlyRemoved = permanentlyRemoved
            it.lore = lore
            it.discovered.addAll(rooms.filter { it.discovered }.map { it.id })
        }

        return gameState
    }

    fun resumeGameFromState(gameState: GameState) {
        rooms.forEach {
            it.exit(this)
            it.discovered = false
        }

        gameState.also {
            shards = it.shards
            player.life = it.playerLife
            player.maxLife = it.playerMaxLife
            player.row = it.playerRow
            player.col = it.playerCol
            player.lens = it.lens
            player.map = it.map
            player.shardScanner = it.shardScanner
            player.loreScanner = it.loreScanner
            player.itemScanner = it.itemScanner
            player.flippers = it.flippers
            player.lightSource = it.lightSource
            player.weapon = it.weapon
            player.armor = it.armor
            player.bracer = it.bracer
            player.jump = it.jump
            player.speed = it.speed
            player.lavaBoots = it.lavaBoots
            player.divineBoots = it.divineBoots

            permanentlyRemoved.clear()
            permanentlyRemoved.addAll(it.permanentlyRemoved)

            lore.clear()
            lore.addAll(it.lore)

            it.discovered.forEach { getRoomById(it).discovered = true }

            loadRoom(it.roomId)
        }

        controller = PlayController()
    }

    private fun saveGame(saveId: SaveId) {
        if (currentRoom.level in 1..2 && player.life != 0) {
            val gameState = getGameState()

            launchImmediately {
                saveState(saveId, gameState)
            }
        }
    }

    fun saveGame() {
        saveGame(saveId)
    }

    fun newGame(saveId: SaveId) {
        val gameState = GameState()

        this.saveId = saveId

        resumeGameFromState(gameState)
    }

    fun addToPermanentlyRemoved(entityId: String) = permanentlyRemoved.add(entityId)

    fun isPermanentlyRemoved(entityId: String) = permanentlyRemoved.contains(entityId)

    fun mouseDownChar(event: CharMouseEvent) = controller.mouseDownChar(this, event)
    fun mouseUpChar(event: CharMouseEvent) = controller.mouseUpChar(this, event)
    fun mouseEnterChar(event: CharMouseEvent) = controller.mouseEnterChar(this, event)
    fun mouseExitChar(event: CharMouseEvent) = controller.mouseExitChar(this, event)


    fun addLoreFromCurrentRoom() {
        lore.add(currentRoom.id)
        val loreId = "${currentRoom.id}_Lore"

        showDialog(loreId)
    }

    fun isLoreDiscovered() = isLoreDiscovered(currentRoom.id)
    fun isLoreDiscovered(roomId: String) = lore.contains(roomId)

    fun quit() {
        running = false
    }

    fun loadGame(saveId: SaveId) {
        this.saveId = saveId
        launchImmediately {
            val gameState = loadState(saveId)
            resumeGameFromState(gameState!!)
        }
    }

    fun loadGame() = loadGame(saveId)
}

